import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Check, Upload, X, FileText, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const UebersichtSubmitPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1); // 1 = Dokumente, 2 = Übersicht
  const [caseId, setCaseId] = useState('');
  const [partnerId, setPartnerId] = useState('');
  const [caseData, setCaseData] = useState(null);
  const [uploads, setUploads] = useState({});
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [truthConfirmed, setTruthConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const cid = searchParams.get('case_id');
    const pid = searchParams.get('partner_id') || localStorage.getItem('partner_id');
    
    if (!cid) {
      navigate('/portal/neuer-fall');
      return;
    }
    
    setCaseId(cid);
    setPartnerId(pid);
    loadCase(cid);
  }, [searchParams, navigate]);

  const loadCase = async (cid) => {
    try {
      // Load case data
      const caseRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cases/${cid}`);
      const caseData = await caseRes.json();
      setCaseData(caseData);
      setDocuments(caseData.documents || []);
      
      // Load images
      const uploadsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads/case/${cid}`);
      const uploadsData = await uploadsRes.json();
      setUploads(uploadsData.slots || {});
    } catch (error) {
      console.error('Load failed:', error);
    }
  };

  const handleDocumentUpload = async (docType, file) => {
    // Check file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      alert('Nur PDF, JPG oder PNG erlaubt');
      return;
    }
    
    // Check file size (25MB)
    if (file.size > 25 * 1024 * 1024) {
      alert('Datei zu groß (max. 25MB)');
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('case_id', caseId);
      formData.append('document_type', docType);
      formData.append('file', file);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads/document`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload fehlgeschlagen');
      }

      await loadCase(caseId);
    } catch (error) {
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentDelete = async (docId) => {
    if (!confirm('Dokument wirklich löschen?')) return;
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/uploads/document/${docId}?case_id=${caseId}`,
        { method: 'DELETE' }
      );

      if (!response.ok) throw new Error('Löschen fehlgeschlagen');
      
      await loadCase(caseId);
    } catch (error) {
      alert(error.message);
    }
  };

  const handleSubmit = async () => {
    if (!truthConfirmed) {
      alert('Bitte bestätigen Sie die Wahrheitserklärung');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/cases/submit?case_id=${caseId}&truth_confirmation=true`,
        { method: 'POST' }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Einreichen fehlgeschlagen');
      }

      // Success - redirect to dashboard
      alert('Fall erfolgreich eingereicht!');
      navigate(`/portal/dashboard?partner_id=${partnerId}`);
    } catch (error) {
      alert(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const STEPS = [
    { number: 1, title: 'Dokumente' },
    { number: 2, title: 'Übersicht' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-5xl mx-auto px-6">
        {/* Progress */}
        <div className="mb-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-8 text-center">Fall abschließen</h1>
          
          <div className="flex items-center justify-center gap-4 mb-8">
            {STEPS.map((step, index) => (
              <React.Fragment key={step.number}>
                <div className="flex flex-col items-center">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold text-lg mb-2 ${
                    step.number < currentStep ? 'bg-brand-green text-white' :
                    step.number === currentStep ? 'bg-brand-green text-white ring-4 ring-brand-green/20' :
                    'bg-gray-200 text-gray-500'
                  }`}>
                    {step.number < currentStep ? <Check className="w-6 h-6" /> : step.number}
                  </div>
                  <span className="text-xs font-medium text-gray-600">{step.title}</span>
                </div>
                {index < STEPS.length - 1 && (
                  <div className={`flex-1 h-1 max-w-[100px] rounded ${step.number < currentStep ? 'bg-brand-green' : 'bg-gray-200'}`}></div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white border border-gray-200 rounded-3xl p-10 shadow-lg">
          {currentStep === 1 && caseData && (
            <Step4Dokumente
              documents={documents}
              onUpload={handleDocumentUpload}
              onDelete={handleDocumentDelete}
              onNext={() => setCurrentStep(2)}
              onBack={() => navigate(`/portal/video-upload?case_id=${caseId}&partner_id=${partnerId}`)}
              loading={loading}
            />
          )}
          {currentStep === 2 && caseData && (
            <Step5Uebersicht
              caseData={caseData}
              uploads={uploads}
              documents={documents}
              truthConfirmed={truthConfirmed}
              setTruthConfirmed={setTruthConfirmed}
              onSubmit={handleSubmit}
              onBack={() => setCurrentStep(1)}
              submitting={submitting}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Step 4: Dokumente (Optional)
const Step4Dokumente = ({ documents, onUpload, onDelete, onNext, onBack, loading }) => {
  const fileInputRefs = {
    registration: React.useRef(),
    invoice: React.useRef(),
    estimate: React.useRef(),
    other: React.useRef()
  };

  const DOC_TYPES = [
    { key: 'registration', title: 'Fahrzeugschein', hint: 'Teil I und/oder Teil II' },
    { key: 'invoice', title: 'Reparaturrechnung', hint: 'Falls bereits vorhanden' },
    { key: 'estimate', title: 'Gutachten / Kostenvoranschlag', hint: 'Falls bereits erstellt' },
    { key: 'other', title: 'Sonstige Dokumente', hint: 'Weitere relevante Unterlagen' }
  ];

  const getDocsForType = (type) => documents.filter(d => d.document_type === type);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Dokumente hochladen</h2>
        <p className="text-gray-600 mb-4">Optional: Laden Sie relevante Dokumente hoch</p>
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <p className="text-sm text-blue-800">
            <strong>Hinweis:</strong> Dokumente sind optional. Sie können diesen Schritt überspringen und direkt zur Übersicht gehen.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {DOC_TYPES.map(docType => {
          const docs = getDocsForType(docType.key);
          
          return (
            <div key={docType.key} className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6">
              <div className="mb-4">
                <h3 className="text-lg font-bold text-gray-900 mb-1">{docType.title}</h3>
                <p className="text-sm text-gray-600">{docType.hint}</p>
                <p className="text-xs text-gray-500 mt-1">PDF, JPG, PNG (max. 25MB)</p>
              </div>

              {/* Uploaded docs */}
              {docs.length > 0 && (
                <div className="space-y-2 mb-4">
                  {docs.map(doc => (
                    <div key={doc.doc_id} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <FileText className="w-4 h-4 text-brand-green flex-shrink-0" />
                        <span className="text-sm text-gray-700 truncate">{doc.original_filename}</span>
                      </div>
                      <button
                        onClick={() => onDelete(doc.doc_id)}
                        className="flex-shrink-0 w-8 h-8 text-red-500 hover:bg-red-50 rounded-lg flex items-center justify-center"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload button */}
              <Button
                onClick={() => fileInputRefs[docType.key].current?.click()}
                disabled={loading}
                variant="outline"
                className="w-full border-2 border-gray-300"
              >
                <Upload className="w-4 h-4 mr-2" />
                {docs.length > 0 ? 'Weiteres hinzufügen' : 'Dokument wählen'}
              </Button>
              <input
                ref={fileInputRefs[docType.key]}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) onUpload(docType.key, file);
                }}
                className="hidden"
              />
            </div>
          );
        })}
      </div>

      <div className="flex justify-between pt-6">
        <Button onClick={onBack} variant="outline" className="border-2 border-gray-300">
          <ArrowLeft className="w-5 h-5 mr-2" />
          Zurück zu Video
        </Button>
        <Button
          onClick={onNext}
          size="lg"
          className="bg-brand-green hover:bg-brand-green-dark text-white px-8 py-4 rounded-xl font-semibold"
        >
          Weiter zur Übersicht
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
      </div>
    </div>
  );
};

// Step 5: Übersicht + Submit
const Step5Uebersicht = ({ caseData, uploads, documents, truthConfirmed, setTruthConfirmed, onSubmit, onBack, submitting }) => {
  const imageCount = Object.values(uploads).reduce((sum, imgs) => sum + imgs.length, 0);
  const hasVideo = caseData.video_uploaded || caseData.video_url;

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Übersicht & Absenden</h2>
        <p className="text-gray-600">Bitte prüfen Sie alle Angaben vor dem Absenden</p>
      </div>

      {/* Auftragsdaten */}
      <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-brand-green" />
          Auftragsdaten
        </h3>
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div><span className="font-semibold">Kennzeichen:</span> {caseData.license_plate}</div>
          <div><span className="font-semibold">Schadentag:</span> {caseData.damage_date}</div>
          <div><span className="font-semibold">Schadenort:</span> {caseData.damage_location}</div>
        </div>
      </div>

      {/* Fahrzeugdaten */}
      <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-brand-green" />
          Fahrzeugdaten
        </h3>
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div><span className="font-semibold">Fahrzeug:</span> {caseData.vehicle_manufacturer} {caseData.vehicle_model}</div>
          <div><span className="font-semibold">KM-Stand:</span> {caseData.vehicle_mileage} km</div>
          <div><span className="font-semibold">Reifen:</span> {caseData.tire_dimension}</div>
          <div><span className="font-semibold">Schadenzone:</span> {caseData.damage_zone}</div>
          <div><span className="font-semibold">Fahrbereit:</span> {caseData.vehicle_drivable ? 'Ja' : 'Nein'}</div>
          <div><span className="font-semibold">Vorschäden:</span> {caseData.pre_damage}</div>
        </div>
      </div>

      {/* Bilder */}
      <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-brand-green" />
          Bilder ({imageCount} Stück)
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(uploads).map(([slotKey, images]) => (
            images.length > 0 && (
              <div key={slotKey} className="text-sm">
                <span className="font-semibold">{slotKey}:</span> {images.length}
              </div>
            )
          ))}
        </div>
      </div>

      {/* Video */}
      <div className={`border rounded-2xl p-6 ${hasVideo ? 'bg-gray-50 border-gray-200' : 'bg-red-50 border-red-300'}`}>
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          {hasVideo ? (
            <CheckCircle className="w-5 h-5 text-brand-green" />
          ) : (
            <X className="w-5 h-5 text-red-600" />
          )}
          Video-Rundgang {!hasVideo && '(FEHLT)'}
        </h3>
        {hasVideo ? (
          <p className="text-sm text-gray-700">✓ Video wurde hochgeladen</p>
        ) : (
          <div>
            <p className="text-sm text-red-700 mb-2 font-semibold">⚠️ Video-Rundgang ist Pflicht!</p>
            <p className="text-xs text-red-600">Bitte gehe zurück und lade ein Video hoch, bevor du den Fall einreichen kannst.</p>
          </div>
        )}
      </div>

      {/* Dokumente */}
      {documents.length > 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-brand-green" />
            Dokumente ({documents.length} Stück)
          </h3>
          <div className="space-y-2">
            {documents.map(doc => (
              <div key={doc.doc_id} className="flex items-center gap-2 text-sm">
                <FileText className="w-4 h-4 text-gray-600" />
                <span className="text-gray-700">{doc.original_filename}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Hinweis */}
      {!hasVideo && (
        <div className="bg-red-50 border-2 border-red-300 rounded-2xl p-6">
          <p className="text-sm text-red-800 leading-relaxed font-semibold">
            ⚠️ <strong>Video fehlt:</strong> Der Fall kann erst eingereicht werden, wenn ein Video-Rundgang hochgeladen wurde. Bitte gehe zurück zum Video-Upload Schritt.
          </p>
        </div>
      )}

      <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6">
        <p className="text-sm text-yellow-800 leading-relaxed">
          <strong>Wichtiger Hinweis:</strong> Alle Angaben und Fotos wurden durch den Partner bereitgestellt. Diese Informationen stellen keine Rechtsberatung dar.
        </p>
      </div>
      
      {/* Read-Only Warnung */}
      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
        <p className="text-sm text-blue-800 leading-relaxed">
          <strong>Achtung:</strong> Nach Einreichung ist der Fall nicht mehr änderbar. Bitte prüfen Sie alle Angaben sorgfältig.
        </p>
      </div>

      {/* Wahrheitserklärung */}
      <div className="bg-white border-2 border-gray-300 rounded-2xl p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Wahrheitserklärung</h3>
        <label className="flex items-start gap-4 cursor-pointer">
          <input
            type="checkbox"
            checked={truthConfirmed}
            onChange={(e) => setTruthConfirmed(e.target.checked)}
            className="mt-1 w-6 h-6 text-brand-green border-gray-300 rounded focus:ring-brand-green"
          />
          <span className="text-sm text-gray-700 leading-relaxed">
            Ich bestätige, dass alle Angaben und Fotos nach bestem Wissen vollständig und korrekt sind. Mir ist bekannt, dass unvollständige oder falsche Angaben zu Rückfragen, Verzögerungen oder Ablehnung führen können.
          </span>
        </label>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-6">
        <Button onClick={onBack} variant="outline" className="border-2 border-gray-300">
          <ArrowLeft className="w-5 h-5 mr-2" />
          Zurück
        </Button>
        <Button
          onClick={onSubmit}
          disabled={!truthConfirmed || submitting || !hasVideo}
          size="lg"
          className="bg-brand-green hover:bg-brand-green-dark text-white px-10 py-6 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'Wird eingereicht...' : 'Fall jetzt einreichen'}
          <Check className="w-5 h-5 ml-2" />
        </Button>
      </div>
    </div>
  );
};

export default UebersichtSubmitPage;
